import React, { useCallback, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { Accordion, AccordionBody, AccordionHeader, AccordionItem } from 'reactstrap';

import Button from '~components/Button';
import { brisbaneCenter, Marker } from '~components/map/common';
import MapComponent from '~components/map/MapComponent';
import RouteInfo from '~components/map/RouteInfo';

import { getGroupMapData } from '~appointments/actions/locations';

import RouteMarker from '../../../components/map/RouteMarker';

/**
 * Props for FormInputGroupRouteMap component.
 * @property appointmentId
 */
interface ComponentProps {
  appointmentId: number;
  isNew?: boolean;
  location?: string;
}

/**
 * Displays a button to generate a route map for a group appointment, and renders the map with route and markers.
 * @param {ComponentProps} props
 */
const FormInputGroupRouteMap: React.FC<ComponentProps> = ({ appointmentId, location, isNew }) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);

  // Store markers in state instead of as a local variable
  const [markers, setMarkers] = useState<Marker[]>([]);

  const [mapData, setMapData] = useState<{
    routeData?: any;
    center: { lat: number; lng: number };
  }>({
    routeData: undefined,
    center: brisbaneCenter,
  });

  const [showMap, setShowMap] = useState(false);
  const [open, setOpen] = useState('');

  const toggle = (id: string) => {
    if (!showMap) return;

    if (open === id) {
      setOpen('');
    } else {
      setOpen(id);
    }
  };

  const handleGenerateMap = useCallback(async () => {
    if (!appointmentId) return;

    setIsLoading(true);
    setShowMap(false);

    try {
      const {
        payload: { success, data },
      } = await dispatch(getGroupMapData({ id: appointmentId }));

      if (success && data) {
        const newMarkers: Marker[] = [];

        // Create client markers with correct order IDs based on waypoint_order
        data.leg_overviews.forEach((wp: any, index: number) => {
          const { waypoint_from } = wp;
          const lat = Number(waypoint_from?.coordinates?.latitude);
          const lng = Number(waypoint_from?.coordinates?.longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return; // skip invalid
          newMarkers.push({
            type: index === 0 ? 'origin' : 'client',
            orderId: index,
            data: {
              name: waypoint_from?.name ?? `Stop ${index}`,
              latitude: lat,
              longitude: lng,
            },
          });
        });

        // Store markers in state
        setMarkers(newMarkers);

        // Find center based on first marker
        const center =
          newMarkers.length > 0
            ? {
                lat: newMarkers[0].data.latitude,
                lng: newMarkers[0].data.longitude,
              }
            : brisbaneCenter;

        setMapData({
          routeData: data,
          center,
        });

        setShowMap(true);
      }
    } catch (error) {
      console.error('Error fetching map data:', error);
    } finally {
      setIsLoading(false);
      setOpen('1');
    }
  }, [appointmentId, dispatch]);

  const renderMarkers = () => {
    return markers.map((marker) => (
      <RouteMarker key={`marker-${marker.orderId}`} type={marker.type} orderId={marker.orderId} data={marker.data} />
    ));
  };

  const isDisabled = isNew || !location || isLoading;

  const defaultRouteOptions = {
    previewProperties: {
      strokeColor: '#FF0000',
      strokeWeight: 4,
      strokeOpacity: 0.7,
    },
  };

  return (
    <div>
      {!isNew && (
        <Accordion open={open} toggle={toggle} className="mx-2">
          <AccordionItem>
            <AccordionHeader targetId="1">
              <div className="d-flex justify-content-between align-items-center w-100">
                <span>Map Route</span>
                {!showMap && (
                  <div>
                    <Button
                      id={`route-map-button`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGenerateMap();
                      }}
                      disabled={isDisabled}
                      type="button"
                      size="sm"
                      className="me-1 p-1"
                    >
                      {isLoading ? 'Generating...' : 'Generate Route Map'}
                    </Button>
                  </div>
                )}
              </div>
            </AccordionHeader>
            <AccordionBody accordionId="1">
              {showMap && mapData.routeData && (
                <div>
                  <div style={{ width: '100%', height: '500px' }}>
                    <MapComponent
                      key={`map-${appointmentId}-${mapData.routeData?.leg_overviews?.length ?? 0}-${markers.length}`}
                      defaultCenter={mapData.center}
                      defaultZoom={12}
                      routeData={mapData.routeData}
                      routeOptions={{
                        ...defaultRouteOptions,
                      }}
                      mapId="DEMO_MAP_ID"
                    >
                      {renderMarkers()}
                    </MapComponent>
                  </div>
                  <div className="mt-3">
                    <RouteInfo routeData={mapData.routeData} markers={markers} />
                  </div>
                </div>
              )}
            </AccordionBody>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
};

export default FormInputGroupRouteMap;
